import {
  Assessment,
  Business,
  Code,
  Dashboard,
  Insights,
  MenuBook,
  PointOfSale,
  Security,
  SupportAgent,
  TrendingUp,
} from "@mui/icons-material";

import type {
  AdminNavSection,
  AdminNavSectionId,
  AdminNavTab,
} from "./adminNavModel.types";

import { ENTRY_POINT_OVERLAP_KEYS } from "@/store/api/endpoints/adminV2EntryPointOverlapApi";

/**
 * Mission Control navigation map (Nav PRD §10–§12, UI rules F-195–F-212):
 * section, then page, then the page's tabs. This is the only place that says
 * where a route lives in the sidebar, breadcrumbs and search.
 *
 * @remarks
 * Every route in `ADMIN_ROUTES` is claimed exactly once: as a page's route, a
 * tab, a fallback, a member (a hidden detail or tool view that belongs to the
 * page for breadcrumbs and the current-page mark), search-only, or unhomed.
 * The route-inventory test enforces this. Access still comes from each
 * route's own gates, so moving a page never widens who can open it.
 */

const tab = (id: string, label: string, routeId: string): AdminNavTab => ({
  id,
  label,
  routeId,
});
const inPageTab = (
  id: string,
  label: string,
  routeId: string,
  addressQuery?: string,
) => ({
  ...tab(id, label, routeId),
  inPage: true,
  ...(addressQuery ? { addressQuery } : {}),
});

export const ADMIN_NAV_SECTIONS: readonly AdminNavSection[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    description: "Today's overview",
    icon: <Dashboard />,
    pages: [
      {
        id: "dashboard",
        label: "Dashboard",
        routeId: "v2_dashboard",
        memberRouteIds: ["dashboard_entry"],
      },
    ],
  },
  {
    id: "crm",
    label: "CRM",
    description: "People, organisations and partners",
    icon: <Business />,
    defaultExpanded: true,
    pages: [
      {
        id: "all_contacts",
        label: "All contacts",
        routeId: "v2_crm",
        aliasRouteIds: ["v2_crm_contacts_path_alias"],
        memberRouteIds: [
          "v2_crm_duplicates",
          "v2_crm_identity_review",
          "v2_crm_contacts_import",
          "v2_crm_contact_record",
          // CRITICAL: hidden overlap drilldowns retain the Contacts home and
          // their card-pair breadcrumb. docs/admin/readmes/CRM_AND_SUPPORT.md
          ...ENTRY_POINT_OVERLAP_KEYS.map(
            (key) => `v2_crm_entry_point_overlap_${key}`,
          ),
        ],
      },
      {
        id: "users",
        label: "Users",
        routeId: "users",
        memberRouteIds: ["user_details", "user_access_audit"],
      },
      {
        id: "organisations",
        label: "Organisations",
        routeId: "organisations",
        fallbackRouteId: "v2_crm_businesses",
        memberRouteIds: ["organisation_details", "v2_crm_business_record"],
      },
      {
        id: "partners",
        label: "Partners",
        routeId: "partners",
        fallbackRouteId: "v2_crm_firms",
        memberRouteIds: [
          "partner_details",
          "v2_crm_firm_record",
          "v2_crm_firm_clients",
          "v2_crm_firm_client_record",
        ],
      },
    ],
  },
  {
    id: "support",
    label: "Support",
    description: "Live alerts, issues, requests and support sessions",
    icon: <SupportAgent />,
    defaultExpanded: true,
    pages: [
      { id: "live_alerts", label: "Live alerts", routeId: "v2_live_alerts" },
      {
        id: "support_conversations",
        label: "Support conversations",
        routeId: "support_conversations",
        memberRouteIds: ["support_conversation_detail"],
      },
      {
        id: "issue_queue",
        label: "Issue queue",
        routeId: "v2_issue_reports",
        memberRouteIds: [
          "v2_issue_report_detail",
          "bug_tracker",
          "bug_tracker_recent",
          "bug_tracker_new",
          "bug_tracker_details",
        ],
      },
      {
        id: "feature_requests",
        label: "Feature requests",
        routeId: "feature_requests",
        memberRouteIds: ["feature_requests_recent", "feature_requests_details"],
      },
      {
        id: "transcript_intake",
        label: "Transcript intake",
        routeId: "transcript_intake",
      },
      {
        id: "spoof_centre",
        label: "Spoof centre",
        routeId: "spoof",
        memberRouteIds: [
          "advanced_spoof",
          "spoof_launcher",
          "support_test_reset",
          "support_preview_keep_list",
        ],
      },
    ],
  },
  {
    id: "messaging",
    label: "Messaging",
    description: "Email, automations, audiences and forms",
    icon: <TrendingUp />,
    defaultExpanded: true,
    pages: [
      {
        id: "emails",
        label: "Emails",
        routeId: "v2_email_comms",
        tabs: [
          tab("broadcasts", "Broadcasts", "v2_email_comms"),
          tab("templates", "Templates", "v2_email_comms_templates"),
          tab("activity", "Activity", "v2_email_comms_activity"),
          tab("settings", "Settings", "v2_email_comms_settings"),
        ],
      },
      {
        id: "automations",
        label: "Automations",
        routeId: "v2_automations",
        tabs: [
          inPageTab("single_step", "Single-step automations", "v2_automations"),
          inPageTab("journeys", "Journey workflows", "v2_automations"),
          inPageTab("global_rules", "Global rules", "v2_automations"),
        ],
      },
      {
        id: "audiences",
        label: "Audiences",
        routeId: "v2_audiences",
        tabs: [
          tab("segments", "Segments", "v2_audiences"),
          tab("tags", "Tags", "v2_audience_tag_library"),
        ],
      },
      {
        id: "forms",
        label: "Forms",
        routeId: "v2_forms",
        tabs: [
          tab("forms", "Forms", "v2_forms"),
          // Forms already addresses its Settings view as ?section=settings.
          inPageTab("settings", "Settings", "v2_forms", "section=settings"),
        ],
        memberRouteIds: ["v2_form_builder"],
      },
    ],
  },
  {
    // D6: the built revenue pages live here, still behind the revenue menu
    // visibility, so the section only shows to staff who can open one.
    id: "sales",
    label: "Sales",
    description: "Billing, offers, coupons, affiliates and the sales pipeline",
    icon: <PointOfSale />,
    pages: [
      {
        id: "billing",
        label: "Billing",
        routeId: "v2_revenue_billing_dashboards",
        tabs: [
          tab("overview", "Billing overview", "v2_revenue_billing_dashboards"),
          tab("subscriptions", "Subscription tools", "subscription"),
          tab("quickbooks_trials", "QuickBooks trials", "quickbooks_trials"),
        ],
      },
      { id: "offers", label: "Offers", routeId: "v2_revenue" },
      { id: "coupons", label: "Coupons", routeId: "v2_revenue_coupons" },
      {
        id: "affiliate_manager",
        label: "Affiliate manager",
        routeId: "v2_revenue_affiliates",
      },
      { id: "sales_pipeline", label: "Sales pipeline", routeId: "v2_sales" },
    ],
  },
  {
    id: "product",
    label: "Product",
    description: "Feature flags and access",
    icon: <Assessment />,
    pages: [
      {
        id: "feature_flags",
        label: "Feature flags",
        routeId: "v2_feature_flags",
        aliasRouteIds: ["v2_feature_flags_product_alias"],
      },
      {
        id: "access",
        label: "Access",
        routeId: "v2_entitlements",
        tabs: [
          tab("entitlements", "Entitlements", "v2_entitlements"),
          tab("ai_insights", "AI insights access", "v2_ai_insights_access"),
          tab("demo_access", "Demo access", "demo_access"),
        ],
      },
    ],
  },
  {
    id: "help",
    label: "Help",
    description: "Help centre, videos and learning",
    icon: <MenuBook />,
    pages: [
      {
        id: "help_centre",
        label: "Help centre",
        routeId: "v2_support_help_centre",
        memberRouteIds: ["v2_support_help_centre_article"],
      },
      {
        id: "help_videos",
        label: "Help videos",
        routeId: "help_videos",
        tabs: [
          tab("videos", "Help videos", "help_videos"),
          tab("suggestions", "Video suggestions", "help_video_suggestions"),
        ],
        memberRouteIds: ["help_video_details", "help_video_suggestion_details"],
      },
      { id: "learning", label: "Learning", routeId: "v2_learning" },
    ],
  },
  {
    id: "insights",
    label: "Insights",
    description: "Business metrics, app usage and AI performance",
    icon: <Insights />,
    pages: [
      {
        id: "business_metrics",
        label: "Business metrics",
        routeId: "statistics",
        tabs: [
          tab("analytics_reports", "Analytics reports", "statistics"),
          tab("platform_metrics", "Platform metrics", "admin_metrics"),
          tab("partner_stats", "Partner stats", "partner_stats"),
        ],
      },
      {
        id: "app_usage",
        label: "App usage",
        routeId: "usage_analytics",
        tabs: [
          tab("usage", "Usage analytics", "usage_analytics"),
          tab("interaction", "Interaction analytics", "interaction_analytics"),
          tab("onboarding", "Onboarding analytics", "onboarding_analytics"),
        ],
      },
      {
        // The page's own funnel tabs (entryPointsCards.ts, Kate's 12 Oct PRD).
        id: "entry_points",
        label: "Entry points",
        routeId: "entry_points",
        tabs: [
          inPageTab("health_check", "Health check", "entry_points"),
          inPageTab("trial", "14-day trial", "entry_points"),
        ],
      },
      {
        id: "ai_performance",
        label: "AI performance",
        routeId: "ai_insights_quality",
        tabs: [
          tab("quality", "AI Insights quality", "ai_insights_quality"),
          tab("openai_usage", "OpenAI usage", "openai_usage"),
        ],
      },
      {
        id: "insights_workspace",
        label: "Insights workspace",
        routeId: "v2_insights_integrations",
        tabs: [
          inPageTab("dashboard", "Dashboard", "v2_insights_integrations"),
          inPageTab(
            "posthog",
            "PostHog dashboards",
            "v2_insights_integrations",
          ),
          inPageTab("taxonomy", "Event taxonomy", "v2_insights_integrations"),
        ],
      },
    ],
  },
  {
    id: "administration",
    label: "Administration",
    description: "Team, audits, exports and guides",
    icon: <Security />,
    belowDivider: true,
    pages: [
      {
        id: "team",
        label: "Team",
        routeId: "v2_team_permissions",
        tabs: [
          tab(
            "team_permissions",
            "Team and permissions",
            "v2_team_permissions",
          ),
          tab("approvals", "Approvals", "v2_approvals"),
        ],
      },
      {
        id: "audits",
        label: "Audits",
        routeId: "v2_audit_log",
        tabs: [
          tab("audit_log", "Audit log", "v2_audit_log"),
          tab("admin_access", "Admin access audit", "admin_access_audit"),
          tab("spoof_sessions", "Spoof session audit", "spoof_sessions_audit"),
          tab("demo_operations", "Demo operations", "demo_ops"),
          tab("xero_referral", "Xero referral audit", "xero_referral_audit"),
        ],
      },
      { id: "csv_exports", label: "CSV exports", routeId: "csv_exports" },
      {
        id: "admin_guides",
        label: "Admin guides",
        routeId: "guides",
        memberRouteIds: ["guide_details", "editable_content_block"],
      },
    ],
  },
  {
    id: "developer",
    label: "Developer",
    description: "Monitoring, logs, integrations and technical tools",
    icon: <Code />,
    belowDivider: true,
    pages: [
      {
        // Diagnosis has no page yet, so its tab is not shown (Nav PRD §2).
        id: "monitoring",
        label: "Monitoring",
        routeId: "platform_status",
        tabs: [
          tab("platform_status", "Platform status", "platform_status"),
          tab("data_quality", "Cashflow data quality", "cashflow_data_quality"),
          tab("release_review", "Release review", "release_review"),
        ],
      },
      {
        id: "logs",
        label: "Logs",
        routeId: "system_logs",
        tabs: [
          tab("system_logs", "System logs", "system_logs"),
          tab("webhook_logs", "Webhook logs", "v2_settings_webhook_logs"),
          tab("job_monitor", "Job monitor", "v2_jobs_monitor"),
        ],
      },
      {
        id: "integrations",
        label: "Integrations",
        routeId: "v2_settings_integrations",
      },
      {
        id: "technical_team_tools",
        label: "Technical team tools",
        routeId: "system_architecture",
        tabs: [
          tab("architecture", "System architecture", "system_architecture"),
          tab("share_links", "Document share links", "docs_share_links"),
        ],
      },
    ],
  },
];

/** Reached by search only; each keeps its current address (Nav PRD §4, §12). */
export const ADMIN_NAV_SEARCH_ONLY: ReadonlyArray<{
  routeId: string;
  label: string;
  sectionId: AdminNavSectionId;
}> = [
  {
    routeId: "business_types",
    label: "Business types",
    sectionId: "administration",
  },
];

/**
 * Removed from the sidebar and search (Nav PRD §11); the address still
 * resolves for old links.
 */
export const ADMIN_NAV_UNHOMED_ROUTE_IDS: readonly string[] = [
  // Product › Navigation dash: "the sidebar does its job".
  "v2_product",
];
